/** Folders used for meal plan / nutrition gallery images on the server. */
export const MEAL_IMAGE_FOLDERS = ["MealPlans", "NutritionGallery"];

export const MEAL_IMAGE_PATH_PATTERN =
  /\/images\/(?:MealPlans|NutritionGallery)\/([^/?#]+)/i;

/** Keep spaces and other characters valid inside an <img src>. */
export const displayMealImageSrc = (src) => {
  if (!src || typeof src !== "string") return "";
  if (src.startsWith("blob:") || src.startsWith("data:")) return src;
  try {
    const absolute = /^https?:/i.test(src);
    const url = new URL(src, "http://localhost");
    url.pathname = url.pathname
      .split("/")
      .map((part) => encodeURIComponent(decodeURIComponent(part)))
      .join("/");
    if (absolute) return url.toString();
    return `${url.pathname}${url.search}`;
  } catch (err) {
    return src.replace(/ /g, "%20");
  }
};

export const mealImageFilename = (url) => {
  if (!url || typeof url !== "string") return null;
  if (url.startsWith("blob:")) return null;
  const match = url.match(MEAL_IMAGE_PATH_PATTERN);
  return match ? match[1] : null;
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export const waitForImageElement = (img) =>
  new Promise((resolve) => {
    if (!img) {
      resolve();
      return;
    }
    if (img.complete && img.naturalHeight > 0) {
      resolve();
      return;
    }
    const done = () => resolve();
    img.onload = done;
    img.onerror = done;
  });

/**
 * Resolve any meal/gallery image URL to a base64 data URL for PDF / html2canvas.
 */
export const resolveMealImageDataUrl = async (src, fetchImageData) => {
  if (!src || typeof src !== "string") return null;

  if (src.startsWith("data:")) {
    return src;
  }

  if (src.startsWith("blob:")) {
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      return await blobToDataUrl(blob);
    } catch (err) {
      console.log("Blob image resolve failed:", err);
      return null;
    }
  }

  if (fetchImageData) {
    try {
      const res = await fetchImageData(src);
      if (res?.data?.status && res.data.data) {
        return res.data.data;
      }
    } catch (err) {
      console.log("API image resolve failed:", err);
    }
  }

  return null;
};

export const preloadMealImagesForPdf = async (sources, fetchImageData) => {
  const unique = [...new Set(sources.filter(Boolean))];
  const map = {};

  await Promise.all(
    unique.map(async (src) => {
      map[src] = await resolveMealImageDataUrl(src, fetchImageData);
    })
  );

  return map;
};

export const applyResolvedImagesToDom = async (rootEl, fetchImageData) => {
  if (!rootEl) return;

  const imgs = rootEl.querySelectorAll(
    ".mpi-opt-photo img, .mpi-option-image img, .mpi-option-preview img, .meal-plan-cell-preview img"
  );
  const sources = [...imgs].map((img) => img.getAttribute("src")).filter(Boolean);
  const map = await preloadMealImagesForPdf(sources, fetchImageData);

  await Promise.all(
    [...imgs].map(async (img) => {
      const src = img.getAttribute("src");
      const dataUrl = map[src];
      if (dataUrl) {
        img.setAttribute("src", dataUrl);
        img.crossOrigin = "anonymous";
        await waitForImageElement(img);
      }
    })
  );
};
